// import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
// import { useNavigate } from 'react-router-dom';
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AuthUser as User } from '../types';

// Remove unused interfaces
// interface AuthResponse extends User { ... }
// interface ApiResponse extends User { ... }

interface AuthContextType {
  user: User | null;
  login: (username: string, password?: string) => Promise<void>;
  register: (username: string, email?: string, password?: string) => Promise<void>;
  logout: () => void;
  checkAuth: () => Promise<void>;
  loading: boolean;
  can_use_ai_assistant: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    checkExistingSession();
  }, []);

  const checkExistingSession = async () => {
    try {
      const response = await fetch('/api/auth/me', {
        credentials: 'include',
      });

      if (response.ok) {
        const userData = await response.json() as User;
        setUser(userData);
      }
      // If not ok, just means no active session - this is normal
    } catch (error) {
      // Log error but don't treat it as a fatal error
      console.error('Session check failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (username: string, password?: string) => {
    const response = await fetch('/api/auth/login', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ username, password }),
      credentials: 'include',
    });

    const data: any = await response.json();
    if (!response.ok) {
      const errorMsg = ('error' in data && data.error) ? data.error : 'Login failed';
      throw new Error(errorMsg);
    }
    setUser(data as User);
  };

  const register = async (username: string, email?: string, password?: string) => {
    const requestData = { username, email, password };
    console.log('Sending registration request:', requestData);
    
    const response = await fetch('/api/auth/register', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestData),
      credentials: 'include',
    });

    const data: any = await response.json();
    console.log('Registration response:', { 
      status: response.status, 
      data,
      error: data.error,
      fullData: JSON.stringify(data, null, 2)
    });
    
    if (!response.ok) {
      const errorMsg = ('error' in data && data.error) ? data.error : 'Registration failed';
      throw new Error(errorMsg);
    }

    setUser(data as User);
  };

  const logout = async () => {
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Logout failed:', error);
    }
    setUser(null);
  };

  const checkAuth = async () => {
    // Implementation of checkAuth
  };

  const value = { user, login, register, logout, checkAuth, loading: isLoading, can_use_ai_assistant: user?.can_use_ai_assistant ?? false };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 